import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { 
  Settings, Plus, X, Mail, Save,
  BarChart3, Package, Users, Truck, Upload
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

export default function SettingsPage() {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/notifications");
      setEmails(res.data.notification_emails || []);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const addEmail = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (emails.includes(newEmail)) {
      toast.error("Email already added");
      return;
    }
    setEmails([...emails, newEmail]);
    setNewEmail("");
  };

  const removeEmail = (emailToRemove) => {
    setEmails(emails.filter(e => e !== emailToRemove));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/settings/notifications", { notification_emails: emails });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="settings-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#003366] text-white hidden lg:block">
        <div className="p-6">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-12 mb-6" />
          <p className="text-sm text-blue-200">Admin Dashboard</p>
        </div>
        <nav className="mt-6">
          <Link to="/admin" className="flex items-center gap-3 px-6 py-3 hover:bg-white/10 text-blue-100">
            <BarChart3 className="h-5 w-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/jobs" className="flex items-center gap-3 px-6 py-3 hover:bg-white/10 text-blue-100">
            <Package className="h-5 w-5" />
            <span>Delivery Jobs</span>
          </Link>
          <Link to="/admin/customers" className="flex items-center gap-3 px-6 py-3 hover:bg-white/10 text-blue-100">
            <Users className="h-5 w-5" />
            <span>Customers</span>
          </Link>
          <Link to="/admin/drivers" className="flex items-center gap-3 px-6 py-3 hover:bg-white/10 text-blue-100">
            <Truck className="h-5 w-5" />
            <span>Drivers</span>
          </Link>
          <Link to="/admin/upload" className="flex items-center gap-3 px-6 py-3 hover:bg-white/10 text-blue-100">
            <Upload className="h-5 w-5" />
            <span>Import Data</span>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 px-6 py-3 bg-white/10 text-white">
            <Settings className="h-5 w-5" />
            <span>Settings</span>
          </Link>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Settings className="h-8 w-8 text-[#003366]" />
              Settings
            </h1>
            <p className="text-slate-600 mt-2">Configure notification and email settings</p>
          </div>

          {/* Notification Emails Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#003366]">
                <Mail className="h-5 w-5" />
                Delivery Notification Emails
              </CardTitle>
              <p className="text-sm text-slate-500">
                These email addresses will automatically receive delivery dockets when a driver completes a delivery.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Emails */}
              <div className="space-y-2">
                {emails.length === 0 ? (
                  <p className="text-slate-400 text-sm py-4 text-center">
                    No notification emails configured yet
                  </p>
                ) : (
                  emails.map((email, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between bg-slate-50 px-4 py-2 rounded-lg"
                      data-testid={`email-${index}`}
                    >
                      <span className="text-slate-700">{email}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEmail(email)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              {/* Add New Email */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="Enter email address"
                    onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                    data-testid="new-email-input"
                  />
                </div>
                <Button
                  onClick={addEmail}
                  variant="outline"
                  className="border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white"
                  data-testid="add-email-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t">
                <Button
                  onClick={saveSettings}
                  disabled={saving}
                  className="w-full bg-[#003366] hover:bg-[#002244]"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Save className="h-4 w-4" />
                      Save Settings
                    </span>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Mobile Navigation */}
      <nav className="lg:hidden mobile-nav bg-white border-t border-slate-200 h-16">
        <div className="h-full flex items-center justify-around">
          <Link to="/admin" className="flex flex-col items-center gap-1 text-slate-400">
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Dashboard</span>
          </Link>
          <Link to="/admin/jobs" className="flex flex-col items-center gap-1 text-slate-400">
            <Package className="h-5 w-5" />
            <span className="text-xs">Jobs</span>
          </Link>
          <Link to="/admin/drivers" className="flex flex-col items-center gap-1 text-slate-400">
            <Truck className="h-5 w-5" />
            <span className="text-xs">Drivers</span>
          </Link>
          <Link to="/admin/settings" className="flex flex-col items-center gap-1 text-[#003366]">
            <Settings className="h-5 w-5" />
            <span className="text-xs">Settings</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
